/**
 * A link in an agent's reply that points at a page of this app.
 *
 * The agent puts a doc's address in its answer as an absolute URL
 * (`https://hirecrew.app/hq/docs/<id>`, from `appOrigin` in `worker/site.ts`).
 * The SPA lives under `/hq` (ADR-0013, the same base as `main.tsx`). That
 * address is a page here, so the reply opens it the way a doc card does.
 * Keep the pages in step with the routes in `main.tsx`.
 *
 * An address on another of Crew's own hosts is one too: the live app, the old
 * workers.dev host (it redirects there, `pageRedirect` in `worker/site.ts`),
 * and this machine's dev server, whatever its port. Each item's id is the
 * same wherever it's opened, so the page opens here.
 */
import { HOME } from "../../app/home";

/** The SPA's base path. */
const BASE = "/hq";

/** Where this tab is. A render with no window (the unit tests) uses the live app. */
export function pageOrigin(): string {
  if (typeof window !== "undefined" && window.location.origin) return window.location.origin;
  return "https://hirecrew.app";
}

/**
 * A path under the basename that `main.tsx` routes: `/`, the top-level pages,
 * one id segment on chats, settings, activity, tasks and docs, Browse apps,
 * an Area's slug, and a join token or a crew slug.
 */
const APP_PAGE =
  /^\/(?:today|login|signup|checkout|setup|memory|people|chats(?:\/[^/]+)?|settings\/integrations\/apps|settings(?:\/[^/]+)?|activity(?:\/[^/]+)?|tasks(?:\/[^/]+)?|docs(?:\/[^/]+)?|areas\/[^/]+|join\/[^/]+|crew\/[^/]+)?$/;

/** Crew's own app hosts besides this tab's: the live app and the old workers.dev host. */
const CREW_ORIGINS = new Set([
  "https://hirecrew.app",
  "https://crew-app.smallcloudinc.workers.dev",
]);

/** A dev server on this machine, on any port. */
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1", "0.0.0.0", "[::1]"]);

const isCrewOrigin = (url: URL, origin: string) =>
  url.origin === origin || CREW_ORIGINS.has(url.origin) || LOCAL_HOSTS.has(url.hostname);

/** A path under the basename that the router has a page for. */
function isAppPage(path: string): boolean {
  return APP_PAGE.test(path);
}

/**
 * The router path (`/docs/<id>`, plus any query or hash) for a link to a page
 * on `origin` or another of Crew's hosts, or null when the link leaves the
 * app. The marketing site is the apex (`https://hirecrew.app/`); only `/hq`
 * is the app.
 */
export function appPageHref(href: string | undefined, origin: string): string | null {
  const raw = href?.trim();
  if (!raw || raw === "streamdown:incomplete-link") return null;
  let url: URL;
  try {
    url = new URL(raw, origin);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (!isCrewOrigin(url, origin)) return null;
  const path = url.pathname;
  if (path !== BASE && !path.startsWith(`${BASE}/`)) return null;
  let rest = path.slice(BASE.length) || "/";
  while (rest.length > 1 && rest.endsWith("/")) rest = rest.slice(0, -1);
  if (!isAppPage(rest)) return null;
  if (rest === "/") rest = HOME;
  return `${rest}${url.search}${url.hash}`;
}

/** One item a page is for: a doc, a task, a chat or an Area. */
export type AppItem = { kind: "doc" | "task" | "chat" | "area"; id: string };

const ITEM_PAGES: [RegExp, AppItem["kind"]][] = [
  [/^\/docs\/([^/?#]+)/, "doc"],
  [/^\/tasks\/([^/?#]+)/, "task"],
  [/^\/chats\/([^/?#]+)/, "chat"],
  [/^\/areas\/([^/?#]+)/, "area"],
];

/** The item a router path (from `appPageHref`) opens, or null for a page that isn't one item's. */
export function appItem(to: string): AppItem | null {
  for (const [page, kind] of ITEM_PAGES) {
    const m = page.exec(to);
    // `/docs/new` is the editor, its draft in the query: a page, not a doc.
    if (m && !(kind === "doc" && m[1] === "new")) return { kind, id: m[1]! };
  }
  return null;
}
